import moment, { Moment } from 'moment-timezone';

const DEFAULT_REGION = 'UTC';

const formatWithRegion = (date: string | Date | number, format: string, region?: string): string => {
  return moment(date)
    .tz(region || DEFAULT_REGION)
    .format(format);
};

/**
 * Converts a given date (string or Date) to a Moment object.
 * If no date is provided, returns current moment.
 */
export const toMoment = (date?: string | Date, region?: string): Moment => {
  return date == null
    ? moment().tz(region || DEFAULT_REGION)
    : typeof date === 'string'
      ? date.trim()
        ? moment.utc(date.trim(), moment.ISO_8601, true).tz(region || DEFAULT_REGION)
        : moment.invalid()
      : moment(date).tz(region || DEFAULT_REGION);
};

export const getStartOfDay = (region?: string): Date => {
  return moment
    .tz(region || DEFAULT_REGION)
    .startOf('day')
    .toDate();
};

export const getEndOfDay = (region?: string): Date => {
  return moment
    .tz(region || DEFAULT_REGION)
    .endOf('day')
    .toDate();
};

export const getCurrentDateTimeISO = (): string => {
  return moment.utc().format('YYYY-MM-DDTHH:mm:ss.SSS');
};

/**
 * Formats a given date (string or Date) to ISO 8601 format (UTC).
 */
export const formatDateToISO = (date?: string | Date): string => {
  return !date ? '' : moment(date).toISOString();
};

export const formatDateTimeLabel = (date?: string | Date | number, region?: string): string => {
  return !date ? ' ' : formatWithRegion(date, 'YYYY-MM-DD HH:mm:ss', region);
};

export const formatDateTimeNSecLabel = (date?: string | Date | number, region?: string): string => {
  return !date ? '' : formatWithRegion(date, 'YYYY-MM-DD HH:mm', region);
};

export const formatTimeNSecLabel = (date?: string | Date, region?: string): string => {
  return !date ? '' : formatWithRegion(date, 'HH:mm', region);
};

export const formatDateLabel = (date?: string | Date, region?: string): string => {
  return !date ? '' : formatWithRegion(date, 'YYYY-MM-DD', region);
};

export const parseServerDate = (dayNum: number | string, region?: string): Moment => {
  const str = String(dayNum);

  if (!/^\d{8}$/.test(str)) {
    return moment.invalid();
  }

  const year = Number(str.slice(0, 4));
  const month = Number(str.slice(4, 6)) - 1;
  const day = Number(str.slice(6, 8));

  return moment.tz(
    {
      year,
      month,
      day
    },
    region || DEFAULT_REGION
  );
};

export const getShortWeekday = (day: number | string, region?: string): string => {
  const dayStr = String(day);

  if (!/^\d{8}$/.test(dayStr)) {
    return '';
  }

  const year = Number(dayStr.slice(0, 4));
  const month = Number(dayStr.slice(4, 6)) - 1;
  const date = Number(dayStr.slice(6, 8));

  return moment
    .tz(
      {
        year,
        month,
        day: date
      },
      region || DEFAULT_REGION
    )
    .format('ddd');
};

export const formatServerDate = (dayNum: number | string, region?: string): string => {
  const m = parseServerDate(dayNum, region);

  return m.isValid() ? m.format('YYYY/MM/DD') : '';
};
